import * as React from "react";
import { useState } from "react";
import { AddCircleIcon } from "@hugeicons/react-pro";
import type { DonationRequestCreated } from "../../../../services/donationRequestService";

type DonorRow = {
  donorIdToken: string;
  name: string;
  nationalId?: string;
  donorType?: string;
};

type Props = {
  t: (k: string, o?: any) => string;
  donors: DonorRow[];
  donorLabel: (name: string, nin?: string) => string;
  selectedDonor: DonorRow | null;
  selectedDonorToken: string;
  setSelectedDonorToken: (v: string) => void;
  submitting: boolean;
  landPrice: number;
  numberSar: (v: number | null | undefined) => string;
  created: DonationRequestCreated | null;
  error: string | null;
  onStartRequest: () => void;
  onGoToDocuments: () => void;
  onOpenBenefactorList: () => void;
};

const nextCtaClass =
  "inline-flex items-center justify-center min-h-[44px] min-w-[160px] rounded-xl bg-sa-600 px-10 py-2.5 text-sm font-semibold text-white hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:opacity-60 disabled:cursor-not-allowed";

/** يطابق زر «التالي» في LandInfoPanel */
const landStyleNextCtaClass =
  "inline-flex items-center justify-center min-h-[48px] min-w-[200px] rounded-2xl bg-sa-600 px-10 py-3 text-sm font-semibold text-white shadow-md shadow-sa-600/25 transition hover:bg-sa-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

const confirmCardHeaderClass =
  "border-b border-neutral-200/80 bg-gradient-to-l from-sa-50/90 via-white to-white px-5 py-3.5 text-center text-base font-semibold text-neutral-900";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-neutral-100 py-2.5 last:border-0 sm:grid-cols-[minmax(7.5rem,10rem)_1fr] sm:items-baseline sm:gap-4">
      <dt className="text-sm font-medium text-neutral-500">{label}</dt>
      <dd className="text-sm font-semibold leading-relaxed text-neutral-900 break-words">{value}</dd>
    </div>
  );
}

function resolveOrderNumber(created: DonationRequestCreated): string {
  const raw = created as unknown as Record<string, unknown>;
  const from = (obj: Record<string, unknown>) => {
    const c = obj.code ?? obj.request_code ?? obj.requestCode;
    if (typeof c === "string" && c.trim() !== "") return c;
    const n = obj.order_number ?? obj.orderNumber ?? obj.id;
    return n != null ? String(n) : null;
  };
  return (
    from(raw) ??
    (raw.data && typeof raw.data === "object" && raw.data !== null ? from(raw.data as Record<string, unknown>) : null) ??
    (created.id != null ? String(created.id) : null) ??
    "—"
  );
}

const ApplicantPanel: React.FC<Props> = ({
  t,
  donors,
  donorLabel,
  selectedDonor,
  selectedDonorToken,
  setSelectedDonorToken,
  submitting,
  landPrice,
  numberSar,
  created,
  error,
  onStartRequest,
  onGoToDocuments,
  onOpenBenefactorList,
}) => {
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  if (created) {
    const orderNumber = resolveOrderNumber(created);
    const kind = (selectedDonor?.donorType || "direct").toLowerCase();
    const kindLabel =
      kind === "direct"
        ? t("donorKindDirect", "مساهم مباشر")
        : t("donorKindProxy", "موكّل");

    return (
      <div className="flex flex-col gap-5" dir="rtl">
        <div className="w-full overflow-hidden rounded-3xl border border-neutral-200/90 bg-white shadow-lg shadow-neutral-200/40 ring-1 ring-black/[0.03]">
          <div className={confirmCardHeaderClass}>{t("applicantStage2Title", "تأكيد إنشاء الطلب")}</div>
          <div className="px-5 py-5 sm:px-6 sm:py-6">
            <div className="overflow-hidden rounded-2xl border border-neutral-200/90 bg-gradient-to-br from-neutral-50/70 via-white to-white shadow-inner">
              <div className="divide-y divide-neutral-200/80">
                <div className="p-4 sm:p-5">
                  <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                    {t("contributorSummary", "المساهم المختار")}
                  </div>
                  <div className="mt-1.5 break-words text-lg font-bold tracking-tight text-neutral-900">
                    {selectedDonor ? donorLabel(selectedDonor.name, selectedDonor.nationalId) : "—"}
                  </div>
                  <div className="mt-3 inline-flex rounded-full border border-emerald-200/80 bg-emerald-50/90 px-3 py-1 text-xs font-semibold text-emerald-900">
                    {kindLabel}
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <h3 className="mb-3 text-right text-sm font-bold text-neutral-900">
                    {t("requestDetailsSection", "تفاصيل الطلب")}
                  </h3>
                  <dl>
                    <DetailRow label={t("creationResult", "نتيجة الإنشاء")} value={t("createdTitle", "تم إنشاء الطلب")} />
                    <DetailRow
                      label={t("orderNumber", "رقم الطلب")}
                      value={<span className="font-mono tabular-nums tracking-tight">{orderNumber}</span>}
                    />
                    {created.state ? (
                      <DetailRow label={t("workflowStatus", "حالة السير")} value={String(created.state)} />
                    ) : null}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-neutral-200/80 bg-gradient-to-b from-white to-neutral-50/80 px-5 py-6 shadow-md shadow-neutral-200/30 sm:px-8">
          <div className="flex flex-col items-center text-center">
            <button type="button" className={landStyleNextCtaClass} onClick={onGoToDocuments}>
              {t("next", "التالي")}
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">{error}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-200 px-4 py-3 font-medium text-neutral-900 text-center">
        {t("applicantTitle", "بيانات مقدم الطلب")}
      </div>
      <div className="p-4">
        {/* Donor select */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            {t("donorLabel", "اختر المساهم")}
          </label>

          <div className="flex gap-2">
            <select
              dir="rtl"
              className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 shadow-xs focus:border-sa-500 focus:outline-none focus:ring-2 focus:ring-sa-500"
              value={selectedDonorToken}
              onChange={(e) => setSelectedDonorToken(e.target.value)}
            >
              {donors.map((d) => (
                <option key={d.donorIdToken} value={d.donorIdToken}>
                  {donorLabel(d.name, d.nationalId)}
                </option>
              ))}
            </select>

            {/* Add Benefactor button */}
            <button
              type="button"
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-sa-300 px-4 py-2 text-sm text-sa-700 hover:bg-sa-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
              title={t("openBenefactorList", "قائمة المساهمين")}
              aria-label={t("addBenefactor", "إضافة مُوكِّل")}
              onClick={onOpenBenefactorList}
            >
              <AddCircleIcon size={18} color="currentColor" variant="stroke" className="shrink-0" />
              <span>{t("addBenefactor", "إضافة مُوكِّل")}</span>
            </button>
          </div>

          {donors.length === 0 && (
            <div className="mt-1 text-sm text-red-600">{t("noDonors", "لا يوجد متبرعون مضافون")}</div>
          )}
        </div>

        {/* Amounts */}
        <div className="mb-4">
          <div className="text-xs text-neutral-500">{t("amountsTitle", "قيمة المساهمة")}</div>
          <div className="flex items-center gap-2 text-3xl font-extrabold text-neutral-900">
            {numberSar(landPrice)}
            <span className="inline-flex items-center text-base">
              <img
                className="riyal-icon h-5 w-5"
                src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                alt="ريال سعودي"
                onError={(e) => {
                  e.currentTarget.replaceWith(
                    Object.assign(document.createElement("span"), {
                      textContent: "﷼",
                      ariaLabel: "ريال سعودي",
                    }) as unknown as Node
                  );
                }}
              />
            </span>
          </div>
        </div>

        {/* الإقرار */}
        <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-50/50 p-3">
          <input
            type="checkbox"
            checked={declarationAccepted}
            onChange={(e) => setDeclarationAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-sa-600 focus:ring-sa-500"
            aria-label={t("declarationLabel", "الإقرار بصحة البيانات")}
          />
          <span className="text-sm text-neutral-700">
            {t("declaration", "أقر بأن جميع البيانات والمستندات التي تم إدخالها صحيحة")}
          </span>
        </label>

        <div className="flex justify-center">
          <button
            className={nextCtaClass}
            disabled={submitting || donors.length === 0 || !declarationAccepted}
            onClick={onStartRequest}
          >
            {submitting ? t("creating", "جاري الإنشاء...") : t("next", "التالي")}
          </button>
        </div>

        {error && (
          <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">{error}</div>
        )}
      </div>
    </div>
  );
};

export default ApplicantPanel;
